/**
 * Component: Carousel
 * ------------------------------------------------------------------------------
 * An interface to create carousel objects using the Swiper plugin.
 * - https://github.com/nolimits4web/swiper
 *
 * @namespace carousel
 */

import Swiper from 'swiper/js/swiper.min';

import {debounce, extendDefaults, isElementInViewport, on} from '~/helpers/utils';

/**
 * DOM selectors.
 */
const selectors = {
  cell: '[js-carousel="cell"]',
  container: '[js-carousel="container"]',
};

/**
 * Create a new carousel instance.
 * @param {String} selector - DOM selector.
 * @param {Object} config - Swiper carousel settings.
 */
export default (selector, config) => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selector),
    cells: [...document.querySelectorAll(config.cellSelector || selectors.cell)],
  };

  /**
   * Instance default settings.
   */
  if (!nodeSelectors.container) {
    throw new Error(`Could not find carousel selector: ${selector}`);
  }

  const namespace = nodeSelectors.container.id || selector;

  const defaults = {
    aria: true,
    swiper: {
      a11y: {
        enabled: true,
      },
      autoplay: false,
      centeredSlides: false,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      loop: true,
      on: {},
      slidesPerView: 1,
      spaceBetween: 16,
      watchSlidesVisibility: true,
    },
  };

  /**
   * Instance settings.
   */
  const swiperSettings = extendDefaults(defaults.swiper, config.swiper);
  config.swiper = swiperSettings;
  const settings = extendDefaults(defaults, config);

  /**
   * Instance globals.
   */
  let carousel = {};

  /**
   * Initialise carousel component.
   */
  function init() {
    if (nodeSelectors.cells.length <= 1) {
      return;
    }

    carousel = new Swiper(nodeSelectors.container, {
      ...settings.swiper,
      on: {
        init: () => {

          /**
           * Offsetting event emitter to trigger.
           */
          window.setTimeout(() => {
            Frame.EventBus.emit(`Carousel-${namespace}:ready`, {
              container: nodeSelectors.container,
              selectedIndex: getSelectedIndex(),
            });
          }, 0);
        },
        transitionEnd: () => {
          Frame.EventBus.emit(`Carousel-${namespace}:settle`, {
            container: nodeSelectors.container,
            selectedIndex: getSelectedIndex(),
          });
        },
      },
    });

    if (settings.aria) {
      setARIAEventListeners();
    }
  }

  /**
   * Set listeners for Carousel and update accessibility markup.
   */
  function setARIAEventListeners() {
    Frame.EventBus.listen(`Carousel-${namespace}:ready`, (section) => {
      updateVisibleCells(section);
      on('resize', debounce(() => updateVisibleCells(section), 500));
    });

    Frame.EventBus.listen(`Carousel-${namespace}:settle`, (section) => {
      updateVisibleCells(section);
    });
  }

  /**
   * Sets tabindex to -1 to links from slides that are not active.
   * @param {HTMLElement} section - Swiper slider.
   */
  function updateVisibleCells(section) {
    const {container} = section;
    [...container.querySelectorAll(selectors.cell)].forEach((cell) => {
      if (!isElementInViewport(container, cell)) {
        updateCellARIA(cell, true, -1);
        return;
      }

      updateCellARIA(cell);
    });
  }

  /**
   * Checks if slide is within Swiper viewport and updates the tabbable
   * elements tabindex value.
   * @param {HTMLElement} cell - Swiper slide.
   * @param {Boolean} isHidden - If slide is not visible in viewport.
   * @param {Number} tabindexValue - Tabindex value.
   */
  function updateCellARIA(cell, isHidden = false, tabindexValue = 0) {
    const tabbableElements = cell.querySelectorAll('a, button');

    cell.setAttribute('aria-hidden', isHidden);

    if (tabbableElements.length) {
      [...tabbableElements].forEach((link) => {
        link.setAttribute('tabindex', tabindexValue);
      });
    }
  }

  /**
   * Trigger update events.
   */
  function update() {
    carousel.update();
  }

  /**
   * Trigger resize events.
   */
  function resize() {
    carousel.update();
  }

  /**
   * Destroy instance.
   */
  function destroy() {
    if (Object.keys(carousel).length === 0) {
      return;
    }

    carousel.destroy();
  }

  /**
   * Updated selected slide using index.
   * @param {Number} index - Image slide index to go to.
   * @param {Number} speed - Optional speed parameter.
   */
  function select(index, speed) {
    carousel.slideToLoop(Number(index), speed);
  }

  /**
   * Get the selected index in carousel.
   * @returns {Number}
   */
  function getSelectedIndex() {
    return carousel.realIndex;
  }

  /**
   * Get the Swiper instance of the carousel.
   * @returns {Object}
   */
  function getSwiperInstance() {
    return carousel;
  }

  /**
   * Check if Swiper instance has been initialised.
   */
  function isInitialised() {
    return (Object.keys(carousel).length !== 0 && !carousel.destroyed);
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    destroy,
    getSelectedIndex,
    getSwiperInstance,
    init,
    isInitialised,
    resize,
    select,
    update,
  });
};
